import { ChangeDetectionStrategy, Component, computed, input, OnInit, Signal, viewChild } from '@angular/core';
import { WebhookSelectComponent } from '../edit-webhook/components/webhook-select/webhook-select.component';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NODE_EDIT_TAB_COMPONENT, NodeEditTab, SelectedNodeForEdit } from 'src/app/shared/flow-chart/flow-chart.model';
import { WaitWebhookNodeData } from '../../../node-edit.type';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { WorkflowModel } from '@models/workflow.model';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { WebhookDialogComponent } from '../edit-webhook/components/webhook-dialog/webhook-dialog.component';
import { UiCopyClipboardComponent } from '@ui';
import { MatTooltip } from '@angular/material/tooltip';
import { WorkflowWebhookModel } from '@models/workflow-webhook.model';
import { WorkflowWebhooksSelectors } from '@states/workflow-webhooks/workflow-webhooks.selector-types';

@Component({
  selector: 'edit-wait-webhook',
  imports: [
    WebhookSelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    UiCopyClipboardComponent,
    MatTooltip,
  ],
  templateUrl: './edit-wait-webhook.html',
  styleUrl: './edit-wait-webhook.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NODE_EDIT_TAB_COMPONENT,
      useExisting: EditWaitWebhook,
    },
  ],
})
export class EditWaitWebhook implements OnInit, NodeEditTab {
  public selectedNodeForEdit = input<SelectedNodeForEdit<WaitWebhookNodeData>>();
  public markSelectAsTouched = input<boolean>(false);
  public disableForm = input<boolean>(false);

  public form!: UntypedFormGroup;

  public webhookSelectSig = viewChild(WebhookSelectComponent);

  public selectNodeSig: Signal<WorkflowModel.NodeConfigDocument> =
    this.store$.selectSignal(WorkflowsSelectors.selectNode);
  public selectWebhooksSig: Signal<
    WorkflowWebhookModel.WebhookEndpointMongoDocument[]
  > = this.store$.selectSignal(
    WorkflowWebhooksSelectors.selectWorkflowWebhooks,
  );

  public disabledWebhooksMapSig: Signal<Map<string, boolean>> = computed(() => {
    const webhooks = this.selectWebhooksSig();

    const map = new Map();

    if (webhooks) {
      const nodeId = this.selectNodeSig()?.nodeId;

      for (const webhook of webhooks) {
        const shouldBeDisable = webhook.target.graphId && webhook.target.nodeId !== nodeId;
        map.set(webhook._id, shouldBeDisable)
      }
    }

    return map;
  })

  constructor(
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private store$: Store,
    private dialog: MatDialog,
  ) { }

  public ngOnInit(): void {
    const timeoutS = this.selectedNodeForEdit()?.data?.timeoutS ?? null;

    this.form = this.fb.group({
      webhookId: [
        this.selectedNodeForEdit()?.data?.webhookId ?? '',
        [Validators.required],
      ],
      name: [
        {
          value: this.selectedNodeForEdit()?.data?.name ??
            this.translateService.instant('FLOW_CHART.NODES.WAITWEBHOOK.NAME'),
          disabled: this.disableForm(),
        },
        [Validators.required],
      ],
      timeoutS: [{
        value: timeoutS,
        disabled: this.disableForm(),
      }, [Validators.required]],
    });

    if (this.markSelectAsTouched()) {
      this.webhookSelectSig()?.markTouched();
    }
  }

  public onWebhookIdChange(webhookId: string): void {
    this.form.controls['webhookId'].setValue(webhookId);
  }

  public edit() {
    const id = this.selectNodeSig().preview.options['webhookId'];

    this.dialog.open(WebhookDialogComponent, {
      panelClass: 'modal-no-padding',
      width: '760px',
      maxHeight: '80vh',
      data: {
        webhookId: id,
      },
    });
  }
}
